import React, { useEffect, useMemo, useState } from "react";
import {
  createTeamMember,
  updateTeamMember,
  getTeamMemberById,
} from "../../../services/teamLigueServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";

const emailOk = (v = "") =>
  !String(v).trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

export default function TeamLigueModal({
  isOpen,
  onClose,
  isEditMode,
  teamMemberId,
  commissions = [],
  onRefresh,
}) {
  const { addNotification } = useNotification();

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    photo_url: "",
    poste: "",
    email: "",
    commission_id: "",
    ordre: 0,
  });

  const [errors, setErrors] = useState({});
  const [loadingMember, setLoadingMember] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const resetForm = () => {
      setFormData({
        nom: "",
        prenom: "",
        photo_url: "",
        poste: "",
        email: "",
        commission_id: "",
        ordre: 0,
      });
      setErrors({});
      setLoadingMember(false);
      setSaving(false);
    };

    if (isEditMode && teamMemberId) {
      const fetchMember = async () => {
        try {
          setLoadingMember(true);
          const data = await getTeamMemberById(teamMemberId);

          setFormData({
            nom: data?.nom || "",
            prenom: data?.prenom || "",
            photo_url: data?.photo_url || "",
            poste: data?.poste || "",
            email: data?.email || "",
            commission_id:
              data?.commission_id === null || data?.commission_id === undefined
                ? ""
                : String(data.commission_id),
            ordre: Number(data?.ordre || 0),
          });
        } catch (err) {
          addNotification("Erreur lors du chargement du membre.", "error");
          console.error("Erreur lors du chargement du membre :", err);
        } finally {
          setLoadingMember(false);
        }
      };

      fetchMember();
    } else {
      resetForm();
    }
  }, [isOpen, isEditMode, teamMemberId, addNotification]);

  const sortedCommissions = useMemo(() => {
    return [...commissions].sort((a, b) =>
      String(a?.nom_commission || "").localeCompare(String(b?.nom_commission || ""), "fr", {
        sensitivity: "base",
      })
    );
  }, [commissions]);

  const previewUrl = useMemo(() => {
    return formData.photo_url?.trim() || "/img/user_avatar.jpg";
  }, [formData.photo_url]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "ordre" ? Number(value || 0) : value,
    }));
  };

  const validate = () => {
    const e = {};

    if (!formData.prenom.trim()) e.prenom = "Le prénom est requis.";
    if (!formData.nom.trim()) e.nom = "Le nom est requis.";
    if (formData.email && !emailOk(formData.email)) {
      e.email = "Email invalide.";
    }
    if (Number.isNaN(Number(formData.ordre))) {
      e.ordre = "L’ordre doit être un nombre.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    const payload = {
      nom: formData.nom.trim(),
      prenom: formData.prenom.trim(),
      photo_url: formData.photo_url.trim(),
      poste: formData.poste.trim(),
      email: formData.email.trim(),
      commission_id:
        formData.commission_id === "" ? null : Number(formData.commission_id),
      ordre: Number(formData.ordre || 0),
    };

    try {
      setSaving(true);

      if (isEditMode) {
        await updateTeamMember(teamMemberId, payload);
        addNotification("Membre mis à jour avec succès.", "success");
      } else {
        await createTeamMember(payload);
        addNotification("Membre créé avec succès.", "success");
      }

      await onRefresh();
      onClose();
    } catch (err) {
      addNotification("Erreur lors de l'enregistrement du membre.", "error");
      console.error("Erreur lors de l'enregistrement du membre :", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="team-ligue-modal-title"
      describedById="team-ligue-modal-desc"
      initialFocusSelector="#team-prenom"
    >
      <ModalHeader
        id="team-ligue-modal-title"
        title={isEditMode ? "Modifier un membre" : "Ajouter un membre"}
        subtitle={
          isEditMode
            ? `Membre #${teamMemberId ?? ""}`
            : "Renseignez les informations du membre de l’équipe Ligue"
        }
        onClose={onClose}
      />

      <ModalBody id="team-ligue-modal-desc">
        {loadingMember ? (
          <div className="py-8 text-center text-sm text-slate-500">
            Chargement du membre…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="text-xs text-slate-600 dark:text-slate-300">
              Les champs marqués d’un <span className="text-pink-600">*</span>{" "}
              sont obligatoires.
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr),220px]">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Prénom" required error={errors.prenom}>
                  <input
                    id="team-prenom"
                    data-autofocus
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass(errors.prenom)}
                    placeholder="Ex : Marie"
                  />
                </Field>

                <Field label="Nom" required error={errors.nom}>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass(errors.nom)}
                    placeholder="Ex : DUPONT"
                  />
                </Field>

                <Field label="Poste" className="sm:col-span-2">
                  <input
                    type="text"
                    name="poste"
                    value={formData.poste}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass()}
                    placeholder="Ex : Président, Secrétaire général..."
                  />
                </Field>

                <Field label="Email" error={errors.email} className="sm:col-span-2">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass(errors.email)}
                    placeholder="exemple@domaine.fr"
                  />
                </Field>

                <Field label="Commission" className="sm:col-span-2">
                  <select
                    name="commission_id"
                    value={formData.commission_id}
                    onChange={handleChange}
                    className={inputClass()}
                  >
                    <option value="">Aucune commission</option>
                    {sortedCommissions.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.nom_commission}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="URL de la photo" className="sm:col-span-2">
                  <input
                    type="text"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    autoComplete="off"
                    className={inputClass()}
                    placeholder="https://..."
                  />
                </Field>

                <Field label="Ordre d’affichage" error={errors.ordre}>
                  <input
                    type="number"
                    name="ordre"
                    value={formData.ordre}
                    onChange={handleChange}
                    className={inputClass(errors.ordre)}
                    placeholder="0"
                  />
                </Field>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Aperçu photo
                </div>

                <div className="mt-4 flex justify-center">
                  <img
                    src={previewUrl}
                    alt="Prévisualisation"
                    onError={(e) => {
                      e.currentTarget.src = "/img/user_avatar.jpg";
                    }}
                    className="h-28 w-28 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                  />
                </div>

                <div className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
                  Laissez vide pour utiliser l’avatar par défaut.
                </div>
              </div>
            </div>
          </form>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          text="Annuler"
          onClick={onClose}
          icon={FaTimes}
          type="button"
          variant="subtle"
          className="!rounded-xl"
        />
        <Button
          text={saving ? "Enregistrement…" : isEditMode ? "Enregistrer" : "Créer"}
          onClick={handleSubmit}
          icon={FaSave}
          type="submit"
          loading={saving}
          disabled={saving || loadingMember}
          className="!rounded-xl"
        />
      </ModalFooter>
    </Modal>
  );
}

function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
        {label} {required ? <span className="text-pink-600">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
      {error ? <p className="mt-1 text-xs text-rose-500">{error}</p> : null}
    </div>
  );
}

function inputClass(error) {
  return `w-full rounded-xl border px-3 py-2 bg-white/80 dark:bg-slate-900/70 ${
    error
      ? "border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
      : "border-slate-300 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
  }`;
}